/**
 * Element action hooks for document state management
 *
 * These hooks dispatch document actions directly to the Zustand store via DocumentStore,
 * bypassing Redux. The DocumentStore handles:
 * 1. Injecting currentUser into actions (previously done by Redux currentUserMiddleware)
 * 2. Forwarding actions to the Zustand store's reduce() method
 *
 * The Zustand store's reduce() method notifies action listeners (ActionRecordService,
 * DocumentSaveService, PatchHandlerService) so middleware functionality is preserved.
 */
import {AddElementsParam, EditorModes} from "@buildwithflux/core";
import {useCallback} from "react";

import {useFluxServices} from "injection/hooks";

import type {DocumentStore} from "../document";

/**
 * Hook to get the addElements action
 *
 * Dispatches directly to Zustand via DocumentStore. The action listener system
 * ensures services (ActionRecordService, DocumentSaveService, PatchHandlerService)
 * receive the action for undo/redo and save functionality.
 *
 * @returns A function to add elements to the document
 */
export function useAddElements(): (params: AddElementsParam, editorMode: EditorModes) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (params: AddElementsParam, editorMode: EditorModes) => {
            documentStore.getState().addElements(params, editorMode === EditorModes.pcb);
        },
        [documentStore],
    );
}

/**
 * Factory function to create element actions for non-React contexts
 *
 * Use this in helper classes, services, or other non-React modules that need
 * to dispatch element actions.
 *
 * @param documentStore - The document dispatcher from FluxServices
 * @returns An object with element action methods
 */
export function createElementActions(documentStore: DocumentStore) {
    return {
        addElements: (params: AddElementsParam, editorMode: EditorModes) => {
            documentStore.getState().addElements(params, editorMode === EditorModes.pcb);
        },
    };
}
